import { Component, inject } from '@angular/core';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FluidModule } from 'primeng/fluid';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { TAGRequest } from '../../../models/tagrequest';
import { ApiService } from '../../api.service';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import {
  newProgressDialogStep,
  openProgressDialog,
  ProgressDialogStep,
  ProgressDialogStepState,
} from '../../components/progressDialog/progressDialog';
import { TAGPermission } from '../../../models/tagpermission';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  ActionConfirmationDialog,
  ActionConfirmationInfo,
} from '../../components/action-confirmation/action-confirmation-dialog';

export interface RequestEditDialogData {
  request: TAGRequest;
}

@Component({
  selector: 'app-internal-requests-edit-dialog',
  imports: [
    TableModule,
    SkeletonModule,
    InputTextModule,
    DialogModule,
    ButtonModule,
    FluidModule,
    ReactiveFormsModule,
    FloatLabelModule,
    TextareaModule,
    DatePickerModule,
    TranslatePipe,
  ],
  providers: [DialogService, TranslateService],
  templateUrl: './requestEditDialog.html',
  styleUrl: './requestEditDialog.scss',
})
export class RequestEditDialog {
  private dialogService = inject(DialogService);
  private translate = inject(TranslateService);
  private api = inject(ApiService);

  requestForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    location: new FormControl(''),
    contact: new FormControl(''),
    contactMail: new FormControl('', [Validators.email]),
    startdate: new FormControl(new Date()),
    enddate: new FormControl(new Date()),
    equipment: new FormControl(''),
  });

  requestIsNew: boolean = true;
  request: TAGRequest;

  constructor(
    public dialogRef: DynamicDialogRef<RequestEditDialog>,
    public config: DynamicDialogConfig<RequestEditDialogData>,
  ) {
    if (config.data?.request) {
      this.request = new TAGRequest(Object.assign({}, config.data.request.serialize()));
      this.requestForm.patchValue(this.request);
      this.requestIsNew = false;
    } else {
      this.request = new TAGRequest();
      this.requestForm.reset();
    }

    if (this.request._id) {
      config.header = this.translate.translate('internal.requests.editTitle', this.request)();
    } else {
      config.header = this.translate.translate('internal.requests.editTitleNew')();
    }
  }

  hasPerm(permission: string): boolean {
    return this.api.user().hasPermission(TAGPermission.fromName(permission));
  }

  convertToEvent() {
    if (this.requestIsNew) return;
    if (!this.requestForm.valid) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.requests.convertTitle', {
          request: this.request,
        })(),
        this.translate.translate('internal.requests.convertMessage', {
          request: this.request,
        })(),
        ActionConfirmationInfo.yesno_yprim,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let updateStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.requests.updateProgress')(),
          ),
        );
        let convertStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.requests.convertProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [updateStep, convertStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          this.request.deserialize(this.requestForm.value);

          try {
            await this.api.updateRequest(this.request);
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
          } catch (reason) {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }

          convertStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));
          try {
            await this.api.promoteRequest(this.request._id);
            convertStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
          } catch (reason) {
            convertStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            convertStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }

          await pDialog.finishDialog();
          this.dialogRef.close(true);
        });
      }
    });
  }

  deleteRequest() {
    if (this.requestIsNew) return;

    ActionConfirmationDialog.openDialog(
      this.dialogService,
      new ActionConfirmationInfo(
        this.translate.translate('internal.requests.deleteTitle', {
          request: this.request,
        })(),
        this.translate.translate('internal.requests.deleteMessage', {
          request: this.request,
        })(),
        ActionConfirmationInfo.yesno_ydanger,
      ),
    ).then((action: string) => {
      if (action === 'yes') {
        let deleteStep = newProgressDialogStep(
          new ProgressDialogStep(
            ProgressDialogStepState.WAITING,
            this.translate.translate('internal.requests.deleteProgress')(),
          ),
        );

        let progressDialog = openProgressDialog(this.dialogService, [deleteStep]);

        progressDialog.onChildComponentLoaded.subscribe(async (pDialog) => {
          deleteStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

          try {
            await this.api.deleteRequest(this.request._id);
            deleteStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          } catch (reason) {
            deleteStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            deleteStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
            return;
          }
        });
      }
    });
  }

  closeDialog(save?: boolean) {
    if (!save) {
      this.dialogRef.close(false);
      return;
    }

    if (!this.requestForm.valid) return;

    let updateStep = newProgressDialogStep(
      new ProgressDialogStep(
        ProgressDialogStepState.WAITING,
        this.translate.translate('internal.requests.updateProgress')(),
      ),
    );

    if (this.requestIsNew) {
      updateStep = newProgressDialogStep(
        new ProgressDialogStep(
          ProgressDialogStepState.WAITING,
          this.translate.translate('internal.requests.createProgress')(),
        ),
      );
    }

    let progressDialog = openProgressDialog(this.dialogService, [updateStep]);

    progressDialog.onChildComponentLoaded.subscribe((pDialog) => {
      updateStep.update((s) => s.setState(ProgressDialogStepState.PROCESSING));

      this.request.deserialize(this.requestForm.value);

      if (this.requestIsNew) {
        this.api
          .newRequest(this.request)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      } else {
        this.api
          .updateRequest(this.request)
          .then(async (result) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.SUCCESS));
            await pDialog.finishDialog();
            this.dialogRef.close(true);
          })
          .catch((reason) => {
            updateStep.update((s) => s.setState(ProgressDialogStepState.ERROR));
            updateStep.update((s) => s.setSubtitle(reason.message));
            pDialog.finishDialog(-1);
            console.log(reason);
          });
      }
    });
  }
}
